/* eslint-disable import/no-unresolved */

// This is safe to do since the Embla modules are mocked and available in consumer
import React, { useEffect } from 'react';
import classNames from 'classnames';
import WheelGesturesPlugin from 'embla-carousel-wheel-gestures';
import useEmblaCarousel from 'embla-carousel-react';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import useScreen from 'hooks/useScreen';

const DEFAULT_CAROUSEL_CONFIG = {
  align: 'start',
  skipSnaps: true,
  draggable: true,
};

const DEFAULT_SLIDE_INDEX_FN = () => {};

const CarouselArrows = ({ variant, goToPrev, goToNext, forceHideButtons }) => {
  const prevNavClassName = classNames(
    'static w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand bg-white',
    '[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray',
    'group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]',
    'pointer-events-auto',
    {
      'rounded-none': variant === 'blog-slider',
      '!bg-transparent border-0 [&_svg_path]:fill-white':
        variant === 'material-swatches-sidebar',
    }
  );

  const nextNavClassName = classNames(
    'static w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand bg-white',
    '[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray',
    'group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]',
    'pointer-events-auto',
    {
      'rounded-none': variant === 'blog-slider',
      '!bg-transparent border-0 [&_svg_path]:fill-white':
        variant === 'material-swatches-sidebar',
    }
  );

  return (
    <div
      className={classNames('absolute inset-x-0 flex pointer-events-none', {
        'hidden md:flex px-0 md:px-[3.333vw] min-[1500px]:px-[50px]':
          variant === 'collections-slider',
        'justify-between -translate-y-[630%]':
          variant !== 'blog-slider' && variant !== 'material-swatches-sidebar',
        'relative hidden md:flex justify-end gap-4 mb-2':
          variant === 'blog-slider',
        'hidden md:flex justify-between -translate-y-[350%]':
          variant === 'material-swatches-sidebar',
        '!hidden': forceHideButtons,
      })}
    >
      <button type="button" className={prevNavClassName} onClick={goToPrev}>
        <IconArrowLeftWhite />
      </button>
      <button type="button" className={nextNavClassName} onClick={goToNext}>
        <IconArrowRightWhite />
      </button>
    </div>
  );
};

export const EmblaSlide = ({ children, className = '' }) => (
  <div
    className={classNames(
      'embla-slide flex-grow-0 flex-shrink-0 min-w-0',
      className
    )}
  >
    {children}
  </div>
);

const EmblaCarousel = ({
  children,
  variant,
  carouselConfig,
  className = '',
  forceHideButtons = false,
  slideIndexFn = DEFAULT_SLIDE_INDEX_FN,
}) => {
  const finalSlides = children;
  const { isMobile } = useScreen();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      ...DEFAULT_CAROUSEL_CONFIG,
      ...carouselConfig,
    },
    [WheelGesturesPlugin()]
  );
  const canScroll = emblaApi?.canScrollNext() || emblaApi?.canScrollPrev();

  const goToPrev = () => emblaApi?.scrollPrev();
  const goToNext = () => emblaApi?.scrollNext();

  useEffect(() => {
    if (!emblaApi) return undefined;

    const updateIndex = () => {
      const index = emblaApi.selectedScrollSnap();
      slideIndexFn(index);
    };

    updateIndex();

    emblaApi.on('scroll', updateIndex);
    emblaApi.on('select', updateIndex);

    return () => {
      emblaApi.off('scroll', updateIndex);
      emblaApi.off('select', updateIndex);
    };
  }, [emblaApi, slideIndexFn]);

  if (!finalSlides.length) {
    return (
      <div
        className={classNames(
          'mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover components [&_.slider-control-bottomcenter]:w-full',
          {
            '!mt-0': variant === 'material-swatches-sidebar',
            '!min-h-[250px] max-[400px]:!min-h-[400px]':
              variant === 'blog-slider',
          }
        )}
      />
    );
  }

  return (
    <div
      className={classNames('embla box-border overflow-hidden', className, {
        'flex flex-col-reverse': variant === 'blog-slider',
        '!pointer-events-none': !canScroll,
      })}
    >
      <div
        className="embla-viewport overflow-clip md:overflow-hidden"
        ref={emblaRef}
      >
        <div className="embla-container flex">{finalSlides}</div>
      </div>
      {variant === 'blog-slider' && isMobile && finalSlides.length > 2 && (
        <span className="text-sm">Scroll for more &gt;</span>
      )}

      <CarouselArrows
        variant={variant}
        goToPrev={goToPrev}
        goToNext={goToNext}
        forceHideButtons={forceHideButtons}
      />
    </div>
  );
};

// export const EmblaSlide = ({ children }) => <div>{children}</div>;
// const EmblaCarousel = () => null;

export default EmblaCarousel;

// TODO: Make sure the following components work/ look as expected:
// * ConsumerCollectionsSlider
// * ConsumerBlogSlider
// * CustomProductList (in Slider view)
// * MultipleBlocks (in Carousel view)
// * FabricGallery (in Slider view)
// * PDP Customer Photos (in Slider view)
// * Customer Reviews (in Slider view)
// * ImageSlider
// * ConsumerTestimonialsSlider (in Slider view)
// * GridWithSidebar
